/* Admin · Motivated-Seller Flagship A/B breakdown.
 *
 * Powers /v3/admin/console/marketing/motivated-seller. Shows capture counts
 * grouped by source (motivated-seller-landing · motivated-seller-tiktok ·
 * motivated-seller-google) so we can see which of the three variants is
 * winning cold traffic.
 *
 * Sections:
 *   1. Top-line stat cards — all-time · last 7d · last 24h · drip completion
 *   2. Variant breakdown (last 7d)  — sorted by count, with % share
 *   3. Variant breakdown (all-time) — same layout
 *   4. Info footer with links to the three landing URLs
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Loader2, RefreshCw, TrendingUp, Users, Mail,
  Zap, ExternalLink, PieChart,
} from "lucide-react";
import { toast } from "sonner";

const VARIANT_LABELS = {
  "motivated-seller-landing":  { label: "Root landing",  route: "/motivated-seller",       hint: "Balanced positioning" },
  "motivated-seller-tiktok":   { label: "TikTok variant", route: "/motivated-seller/fast",  hint: "Emotional · short-form" },
  "motivated-seller-google":   { label: "Google variant", route: "/motivated-seller/guide", hint: "Keyword-heavy · SEO" },
};

function labelFor(source) {
  return VARIANT_LABELS[source]?.label || source;
}
function routeFor(source) {
  return VARIANT_LABELS[source]?.route;
}
function hintFor(source) {
  return VARIANT_LABELS[source]?.hint;
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[var(--line)] bg-white p-5 shadow-sm"
    >
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[var(--ink-2)]">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-2 text-3xl font-black text-[var(--ink)] font-display">
        {value.toLocaleString()}
      </div>
      {sub ? <div className="mt-0.5 text-xs text-[var(--ink-2)]">{sub}</div> : null}
    </div>
  );
}

function VariantTable({ rows, total, testid }) {
  if (!rows.length) {
    return (
      <div className="text-sm text-[var(--ink-2)] italic py-4 px-1">
        No captures in this window yet.
      </div>
    );
  }
  return (
    <div data-testid={testid} className="overflow-hidden rounded-xl border border-[var(--line)] bg-white">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-[11px] uppercase tracking-wider text-[var(--ink-2)]">
          <tr>
            <th className="text-left px-4 py-2.5 font-semibold">Variant</th>
            <th className="text-left px-4 py-2.5 font-semibold">Source key</th>
            <th className="text-right px-4 py-2.5 font-semibold">Captures</th>
            <th className="text-right px-4 py-2.5 font-semibold">Share</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--line)]">
          {rows.map((r) => {
            const pct = total > 0 ? (100 * r.count) / total : 0;
            const route = routeFor(r.source);
            return (
              <tr key={r.source} data-testid={`ms-variant-row-${r.source}`} className="hover:bg-[var(--bg)]/60">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[var(--ink)]">{labelFor(r.source)}</div>
                  {hintFor(r.source) ? (
                    <div className="text-xs text-[var(--ink-2)]">{hintFor(r.source)}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--ink-2)]">
                  {route ? (
                    <a href={route} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[var(--primary)]">
                      {r.source} <ExternalLink size={11} />
                    </a>
                  ) : r.source}
                </td>
                <td className="px-4 py-3 text-right font-bold text-[var(--ink)]">{r.count.toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-24 h-1.5 bg-[var(--bg)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[var(--primary)]"
                        style={{ width: `${Math.min(100, pct).toFixed(1)}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold tabular-nums text-[var(--ink)] w-10 text-right">
                      {pct.toFixed(1)}%
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminMotivatedSellerAB() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/marketing/motivated-seller/breakdown");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load breakdown");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="p-6" data-testid="ms-admin-loading">
        <Loader2 className="animate-spin text-[var(--primary)]" />
      </div>
    );
  }
  if (!data) return null;

  const totals = data.totals || {};
  const dripActive = data.drip?.active ?? 0;
  const dripCompleted = data.drip?.completed_sequence ?? 0;
  const dripCompletionPct = dripActive > 0
    ? (100 * dripCompleted / dripActive).toFixed(1)
    : "0.0";

  const bySource7d = data.by_source?.last_7d || [];
  const bySourceAll = data.by_source?.all_time || [];

  return (
    <div className="max-w-[1180px] mx-auto p-6 space-y-8" data-testid="admin-motivated-seller-ab">
      {/* header */}
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--primary)] mb-1">
            Marketing · Flagship funnel
          </div>
          <h1 className="text-3xl font-black font-display text-[var(--ink)]">
            Motivated Seller · A/B breakdown
          </h1>
          <p className="text-sm text-[var(--ink-2)] mt-1 max-w-2xl">
            Live capture counts for <code className="font-mono text-xs bg-[var(--bg)] px-1.5 py-0.5 rounded">motivated-seller-30-day</code> broken down by landing-page variant. Use this to compare TikTok/paid-social vs Google/organic conversion.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="ms-admin-refresh"
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm font-semibold hover:bg-[var(--bg)]"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </header>

      {/* stat cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="ms-stat-all-time" icon={Users}      label="All-time captures" value={totals.all_time || 0} sub="Since flagship launch" />
        <StatCard testid="ms-stat-7d"       icon={TrendingUp} label="Last 7 days"        value={totals.last_7d || 0}   sub={totals.last_7d === 1 ? "capture" : "captures"} />
        <StatCard testid="ms-stat-24h"      icon={Zap}        label="Last 24 hours"      value={totals.last_24h || 0}  sub="Today's momentum" />
        <StatCard testid="ms-stat-drip"     icon={Mail}       label="Drip completion"    value={dripCompleted}         sub={`${dripCompletionPct}% of ${dripActive} enrolled reached Day 21`} />
      </section>

      {/* Last 7d variant winner */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <PieChart size={14} className="text-[var(--primary)]" />
          <h2 className="text-lg font-bold font-display text-[var(--ink)]">Last 7 days · by variant</h2>
        </div>
        <VariantTable
          testid="ms-variants-7d"
          rows={bySource7d}
          total={totals.last_7d || 0}
        />
      </section>

      {/* All-time */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <PieChart size={14} className="text-[var(--ink-2)]" />
          <h2 className="text-lg font-bold font-display text-[var(--ink)]">All-time · by variant</h2>
        </div>
        <VariantTable
          testid="ms-variants-alltime"
          rows={bySourceAll}
          total={totals.all_time || 0}
        />
      </section>

      {/* footer links */}
      <footer className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--bg)] p-5 text-sm text-[var(--ink-2)]">
        <div className="font-semibold text-[var(--ink)] mb-2">Landing URLs</div>
        <ul className="space-y-1">
          <li>
            <Link to="/motivated-seller" target="_blank" className="text-[var(--primary)] font-semibold inline-flex items-center gap-1">
              /motivated-seller <ExternalLink size={12} />
            </Link>{" "}
            <span className="text-xs">— root · balanced copy</span>
          </li>
          <li>
            <Link to="/motivated-seller/fast" target="_blank" className="text-[var(--primary)] font-semibold inline-flex items-center gap-1">
              /motivated-seller/fast <ExternalLink size={12} />
            </Link>{" "}
            <span className="text-xs">— TikTok / paid-social variant</span>
          </li>
          <li>
            <Link to="/motivated-seller/guide" target="_blank" className="text-[var(--primary)] font-semibold inline-flex items-center gap-1">
              /motivated-seller/guide <ExternalLink size={12} />
            </Link>{" "}
            <span className="text-xs">— Google / SEO variant</span>
          </li>
        </ul>
        <div className="mt-3 text-xs text-[var(--ink-3)]">
          Generated: {data.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}
        </div>
      </footer>
    </div>
  );
}
